/**
 * Generic resource detail page backed by the authenticated experience API.
 */
import type { ReactNode } from 'react';
import { DataDetailPage } from './DataDetailPage';
import { GenericResourceFormPage } from './GenericResourceFormPage';
import { PageForbiddenState, PageLoadingState, PageNotFoundState } from '../states';
import { ResourceLoadErrorState } from './ResourceLoadErrorState';
import { useExperience } from '../../features/experience/ExperienceProvider';
import {
  useGenericResourceRecord,
  useResolvedResource,
} from '../../features/experience/useResolvedExperience';

interface GetHookResult {
  data?: unknown;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null | unknown;
  refetch: () => void;
}

export interface GenericResourceDetailPageProps {
  resourceId: string;
  useGet?: (id: string) => GetHookResult;
  recordId?: string;
  paramName?: string;
  /** Domain-specific sections rendered only after the parent resource is authorized. */
  children?: ReactNode;
}

export function GenericResourceDetailPage({
  resourceId,
  useGet,
  recordId,
  paramName,
  children,
}: GenericResourceDetailPageProps) {
  const experience = useExperience();
  const descriptorQuery = useResolvedResource(resourceId);
  const genericRecordQuery = useGenericResourceRecord<Record<string, unknown>>(
    resourceId,
    recordId,
  );

  if (descriptorQuery.isLoading || experience.loading) return <PageLoadingState />;
  if (descriptorQuery.isError || experience.error) {
    return (
      <ResourceLoadErrorState
        error={descriptorQuery.error ?? experience.error}
        onRetry={() => void Promise.all([
          descriptorQuery.refetch(),
          experience.refresh(),
        ])}
      />
    );
  }

  const resolved = descriptorQuery.data;
  if (!resolved) return <PageNotFoundState />;
  if (resolved.availability !== 'AVAILABLE' && resolved.availability !== 'READ_ONLY') {
    return <PageForbiddenState reasonCode={resolved.reasonCode} decisionId={resolved.decisionId} />;
  }

  const resource = resolved.resource;
  if (!resource.detailRouteId || !resource.readOperationId) return <PageNotFoundState />;
  const backPath = experience.getRoutePath(resource.listRouteId);
  if (!backPath) return <PageNotFoundState />;

  // Backend view descriptors are the authority for organization detail
  // surfaces. Render the same field/action contract used by the form routes;
  // resources without a view descriptor retain the table-detail renderer.
  if (resolved.forms.view) {
    return (
      <GenericResourceFormPage
        resourceId={resourceId}
        mode="view"
        recordId={recordId}
      />
    );
  }

  const recordHook = useGet ?? (() => genericRecordQuery);

  return (
    <DataDetailPage
      titleKey={String(resource.detailTitleKey ?? resource.listTitleKey)}
      backPath={backPath}
      paramName={paramName}
      useGet={recordHook}
      descriptor={resolved.table}
    >
      {children}
    </DataDetailPage>
  );
}
